import { makeStyles } from "tss-react/mui"
import { Box, DialogContent, Divider, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import {
    UserDetails,
    fetchProductDetailsForUser,
    FetchProductDetailsForUserResponse,
} from "../../../../../../modules/nonAdminUser/nonAdminUserApi"

import { useMoney } from "../../../../../../modules/currency/useMoney"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardDialog } from "../../../../dialogs/StandardDialog"
import { LoadingSpinner } from "../../../../loading/LoadingSpinner"
import ProductImg from "./ProductImg"

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        maxWidth: "50rem",
    },
    dialogContent: {
        padding: "1rem 4rem 4rem",
        display: "grid",
        gap: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    box: {
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "5fr 1fr",
    },
    price: {
        textAlign: "right",
        flex: "none",
    },
    description: {
        color: theme.palette.tailwind.slate[500],
    },
}))

const transformData = (data: FetchProductDetailsForUserResponse) => data.product

type ProductDetailsDialogProps = Readonly<{
    userId: number
    productId: number
    handleClose: () => void
}>

export function ProductDetailsDialog({ userId, productId, handleClose }: ProductDetailsDialogProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const { data: product, isLoading } = useApiQuery(fetchProductDetailsForUser, {
        queryName: UserDetails.USER_PRODUCT_DETAILS,
        dependencies: {
            userId,
            productId,
        },
        enabled: Boolean(userId) && Boolean(productId),
        select: transformData,
    })

    return (
        <StandardDialog
            open={!!product}
            onClose={handleClose}
            dialogTitle={product?.name || ""}
            slotProps={{
                paper: { className: classes.dialogPaper },
            }}
            maxWidthProp="md"
        >
            <DialogContent className={classes.dialogContent}>
                {!isLoading && product ? (
                    <>
                        {product?.productPhoto?.url && (
                            <ProductImg imageUrl={product?.productPhoto?.url} size="large" />
                        )}
                        <Box className={classes.box}>
                            <Typography variant="subtitle1">{product.description}</Typography>
                            <Typography variant="subtitle1" className={classes.price}>
                                {moneyFactory.format(toDinero(product.price))}
                            </Typography>
                        </Box>
                        {!!product.productDescription?.allergens.length && (
                            <>
                                <Divider flexItem />
                                <Box className={classes.box}>
                                    <Box>
                                        <LocalizedStrict id="user-home-week-menu-dialog-allergens">
                                            <Typography variant="subtitle1" className={classes.description}>
                                                Allergens:
                                            </Typography>
                                        </LocalizedStrict>
                                        <Typography variant="subtitle1">
                                            {product.productDescription.allergens.map((el) => el.name).join(" - ")}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <LoadingSpinner />
                )}
            </DialogContent>
        </StandardDialog>
    )
}
